import { DataQualityType, localizeDataQualityTypeName } from '@tableau/data-catalog-client-components';
import {
  CheckBoxWithLabelWidget,
  DropdownButtonType,
  getMaxCharValidation,
  List,
  ListProps,
  SingleSelectDropdown,
  TextLinkWidget
} from '@tableau/widgets';
import * as React from 'react';
import * as shallowEqual from 'shallowequal';

import { HelpService } from '~commonInfra/services/HelpService';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { Messages } from '../../codegen/Localize';
import { ReactActionDialogBodyProps, ReactActionDialogOptions } from '../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { TextFormatterOrAreaWidget } from '../content/components/TextFormatterOrAreaWidget';
import {
  checkboxStyle,
  ContentItemNameDiv,
  DATA_QUALITY_DIALOG_HELP_TEST_ID,
  DATA_QUALITY_DIALOG_MESSAGE_ID,
  DATA_QUALITY_DIALOG_SEVERE_TOGGLE_TEST_ID,
  DATA_QUALITY_DIALOG_TOGGLE_TEST_ID,
  DATA_QUALITY_DIALOG_TYPE_TEST_ID,
  DropDownPromptDiv,
  dropDownStyle,
  HelpTextDiv,
  InputHintDiv,
  markupEditorStyle,
  textAreaStyle,
  textAreaWidgetClass} from '../content/styles/DataQualityStyles';
import {
  dataQualityTypeListItem,
  dataQualityTypeToString,
  stringToDataQualityType,
  userEditableDataQualityTypes, } from './DataQualityType';

const MESSAGE_MAXIMUM_LENGTH: number = 4000;

export const defaultInitialState: DataQualityDialogState = {
  isActive: false,
  isSevere: false,
  type: dataQualityTypeToString(DataQualityType.WARNING),
  message: '',
};

export interface DataQualityDialogBodyProps {
  readonly contentName: string;
}

export interface DataQualityDialogState {
  readonly isActive: boolean;
  readonly isSevere: boolean;
  readonly type: string;
  readonly message: string;
}

function isTextAreaValid(currentFormData: { message: string, isActive: boolean }): boolean {
  return (!currentFormData.isActive || currentFormData.message.length > 0) && currentFormData.message.length <= MESSAGE_MAXIMUM_LENGTH;
}

// TODO: move this somewhere shared
export function isFormDataValidForSubmission<T extends { message: string }>(args: {
  initialFormData: T;
  currentFormData: T;
  additionalValidation: (current: T) => boolean;
}): boolean {
  const { initialFormData, currentFormData } = args;

  // We don't allow the user to submit the original data
  const formIsDirty = !shallowEqual(initialFormData, currentFormData);
  if (!formIsDirty) {
    return false;
  }

  if (!args.additionalValidation(currentFormData)) {
    return false;
  }

  return true;
}

export function SevereWarningContent(props: {
  disabled: boolean;
  isSevere: boolean;
  showHelpText: boolean;
  setIsSevere: (b: boolean) => void;
}): JSX.Element | null {
  return (
    <>
      <CheckBoxWithLabelWidget
        label={Messages.dataQualityDialog_IsSevereCheckboxLabel()}
        testId={DATA_QUALITY_DIALOG_SEVERE_TOGGLE_TEST_ID}
        checked={props.isSevere}
        handleChange={props.setIsSevere}
        removeLeftMargin={true}
        disabled={props.disabled}
        containerStyle={checkboxStyle} />
      {
        props.showHelpText &&
          <HelpTextDiv>{ Messages.dataQualityDialog_IsSevereHelp() }</HelpTextDiv>
      }
    </>
  );
}

export type DataQualityActionDialogOptions = ReactActionDialogOptions<DataQualityDialogBodyProps, DataQualityDialogState, unknown>;
export type DataQualityDialogBodyComponentProps = ReactActionDialogBodyProps<DataQualityDialogBodyProps, DataQualityDialogState>;

export function DataQualityDialogBody(props: DataQualityDialogBodyComponentProps): React.ReactElement {
  const handleMessageChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => props.updateDialogState({ message: e.currentTarget.value }),
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState]
  );
  const {message, valid} = getMaxCharValidation(props.dialogState.message, MESSAGE_MAXIMUM_LENGTH);

  const useMarkup = FeatureFlags.markupEditingEnabled;

  return (
    <>
      <ContentItemNameDiv>{props.contentName}</ContentItemNameDiv>
      <CheckBoxWithLabelWidget
        label={Messages.dataQualityDialog_IsActiveCheckboxLabel()}
        testId={DATA_QUALITY_DIALOG_TOGGLE_TEST_ID}
        checked={props.dialogState.isActive}
        handleChange={(isActive: boolean) => props.updateDialogState({ isActive })}
        removeLeftMargin={true}
        containerStyle={checkboxStyle} />

      <HelpTextDiv>
        {Messages.dataQualityDialog_IsActiveHelp()}
        <TextLinkWidget
          href={HelpService.getServerHelpUrlByCategory('dqw-manual')}
          testId={DATA_QUALITY_DIALOG_HELP_TEST_ID}
          target="_blank" rel="noopener noreferrer"
        >
          {Messages.dataQuality_learnMore()}
        </TextLinkWidget>
      </HelpTextDiv>

      <DropDownPromptDiv>{Messages.dataQualityDialog_DropdownPrompt()}</DropDownPromptDiv>
      <InputHintDiv>{Messages.dataQualityType()}</InputHintDiv>
      <SingleSelectDropdown
        testId={DATA_QUALITY_DIALOG_TYPE_TEST_ID}
        type={DropdownButtonType.Outline}
        list={DataQualityWarningTypeList(props.dialogState.type)}
        disabled={!props.dialogState.isActive}
        value={localizeDataQualityTypeName(stringToDataQualityType(props.dialogState.type))}
        onChange={(type?: string) => props.updateDialogState({ type })}
        style={dropDownStyle}
        label={''} />
      <SevereWarningContent
        disabled={!props.dialogState.isActive}
        isSevere={props.dialogState.isSevere}
        showHelpText={true}
        setIsSevere={(isSevere: boolean) => props.updateDialogState({ isSevere })}
      />

      <TextFormatterOrAreaWidget
        label={<InputHintDiv>{Messages.dataQualityMessage()}</InputHintDiv>}
        onChange={handleMessageChange}
        id={DATA_QUALITY_DIALOG_MESSAGE_ID}
        testId={DATA_QUALITY_DIALOG_MESSAGE_ID}
        disabled={!props.dialogState.isActive}
        valid={valid}
        message={message}
        className={useMarkup ? null : textAreaWidgetClass}
        style={useMarkup ? markupEditorStyle : textAreaStyle}
        markup={props.dialogState.message}
      />
    </>
  );
}

function DataQualityWarningTypeList(selected: string): React.ReactElement<ListProps> {
  return <List items={userEditableDataQualityTypes.filter(type => type !== DataQualityType.CERTIFIED).map(dataQualityTypeListItem)} selectedValue={selected.toString()} />;
}

export function getDataQualityActionDialogOptions(args: {
  initialBodyProps: DataQualityDialogBodyProps;
  initialFormData: DataQualityDialogState;
  doAction: (formData: DataQualityDialogState) => Promise<unknown>;
}): DataQualityActionDialogOptions {

  const { initialBodyProps, initialFormData, doAction } = args;

  return {
    title: Messages.dataQualityDialog_Title(),
    bodyComponent: DataQualityDialogBody,
    initialDialogState: initialFormData,
    initialBodyProps: initialBodyProps,
    actionName: Messages.save(),
    action: (_, dialogState) => doAction(dialogState),
    actionDisabled: (_, dialogState) => !isFormDataValidForSubmission({
      initialFormData,
      currentFormData: dialogState,
      additionalValidation: isTextAreaValid
    }),
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width500px,
  };
}
